import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Send, MessageCircle, Shield, AlertTriangle, UserCheck, Flag, Image as ImageIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { TradesmanLogo } from "./TradesmanLogo";
import { ReportChatDialog } from "./ReportChatDialog";
import { ReportTradesmanAccountDialog } from "./ReportTradesmanAccountDialog";
import { ChatPhotoUpload } from "./ChatPhotoUpload";

interface Message {
  id: string;
  sender_id: string;
  content: string;
  message_type: string;
  photo_url?: string;
  is_blocked: boolean;
  blocked_reason?: string;
  created_at: string;
  sender: {
    full_name: string;
    user_type: string;
  };
  recipient: {
    full_name: string;
    user_type: string;
  };
}

interface Job {
  id: string;
  title: string;
  customer_id: string;
  tradesman_id?: string;
}

interface JobMessagingDialogProps {
  job: Job | null;
  onClose: () => void;
  recipientId?: string; // Optional specific recipient ID for messaging responding tradesmen
}

export const JobMessagingDialog = ({ job, onClose, recipientId }: JobMessagingDialogProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [otherPartyProfile, setOtherPartyProfile] = useState<any>(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showAccountReportDialog, setShowAccountReportDialog] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [photoViewerUrl, setPhotoViewerUrl] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [typingIndicator, setTypingIndicator] = useState<string | null>(null);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const realtimeChannelRef = useRef<any>(null);
  // Track if user is near bottom to avoid jumpy auto-scroll
  const isNearBottomRef = useRef(true);
  const lastMessageIdRef = useRef<string | null>(null);

  const getViewportEl = () => {
    return (scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement) || null;
  };

  useEffect(() => {
    if (job) {
      console.log('JobMessagingDialog: Initializing for job:', job.id, 'recipientId:', recipientId);
      try {
        fetchCurrentUserProfile();
        fetchOtherPartyProfile();
        fetchMessages();
        setupRealtimeSubscription();
        checkPaymentStatus();
        markMessagesAsRead();
        startChatSession();
      } catch (error) {
        console.error('JobMessagingDialog: Error during initialization:', error);
        toast({
          title: "Error",
          description: "Failed to initialize chat. Please try again.",
          variant: "destructive",
        });
      }
    }

    // Cleanup on unmount
    return () => {
      console.log('JobMessagingDialog: Cleaning up');
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
      endChatSession();
    };
  }, [job, recipientId]);

  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const own = currentUserProfile?.id === last.sender_id;

    // Auto-scroll to new messages
    const scrollToBottom = () => {
      const viewport = getViewportEl();
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      } else if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: own ? "smooth" : "auto" });
      }
    };

    // Use setTimeout to ensure DOM has updated
    setTimeout(scrollToBottom, 100);
    
    // Update chat session to show user is active
    updateChatSession();
    lastMessageIdRef.current = last.id;
  }, [messages, currentUserProfile?.id]);

  // Ensure messages are marked as read once we know the current user and the job
  useEffect(() => {
    if (job && currentUserProfile) {
      console.log('JobMessagingDialog: Marking messages as read - job changed or user loaded');
      markMessagesAsRead();
      updateChatSession(); // Update that user is actively viewing messages
    }
    // Only run when job or user changes, NOT when messages change
  }, [job?.id, currentUserProfile?.id]);

  // Set up interval to update chat session every 30 seconds while dialog is open
  useEffect(() => {
    const interval = setInterval(() => {
      updateChatSession();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [job]);

  // Observe scroll position to control auto-scroll behavior
  useEffect(() => {
    const viewport = getViewportEl();
    if (!viewport) return;

    const onScroll = () => {
      const threshold = 100; // px from bottom to consider "near bottom"
      const distanceFromBottom = viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop;
      isNearBottomRef.current = distanceFromBottom <= threshold;
    };

    // Initialize and bind
    onScroll();
    viewport.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [job?.id]);

  const fetchCurrentUserProfile = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching current user profile:", error);
        toast({
          title: "Error",
          description: "Failed to load user profile. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      if (!profile) {
        console.error("No profile found for current user");
        toast({
          title: "Error",
          description: "User profile not found. Please complete your profile setup.",
          variant: "destructive",
        });
        return;
      }

      setCurrentUserProfile(profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchOtherPartyProfile = async () => {
    if (!job) return;
    
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: currentProfile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.user.id)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching current profile:", profileError);
        return;
      }
      
      if (!currentProfile) {
        console.error("Current profile not found");
        return;
      }

      console.log('Current profile:', currentProfile);
      console.log('Job details:', job);
      console.log('Recipient ID prop:', recipientId);

      // Determine the other party's ID - improved logic for chat reporting
      let otherPartyId = recipientId;
      
      if (!otherPartyId) {
        if (currentProfile.user_type === 'customer') {
          // Customer wants to message a tradesman - prefer tradesman_id, but also check for any messages
          otherPartyId = job.tradesman_id;
          
          // If no assigned tradesman, try to find from messages for reporting purposes
          if (!otherPartyId) {
            console.log('No assigned tradesman, checking messages for other party...');
            try {
              const { data: messageData, error: msgError } = await supabase
                .from('messages')
                .select('sender_id, recipient_id')
                .eq('job_id', job.id)
                .neq('sender_id', currentProfile.id)
                .limit(1);
              
              if (!msgError && messageData && messageData.length > 0) {
                otherPartyId = messageData[0].sender_id;
                console.log('Found other party from messages:', otherPartyId);
              }
            } catch (error) {
              console.error('Failed to find other party from messages:', error);
            }
          }
        } else if (currentProfile.user_type === 'tradesman') {
          // Tradesman wants to message customer - always use customer_id
          otherPartyId = job.customer_id;
        }
      }
      
      // Additional fallback for tradesmen: try to get customer_id from job_responses
      if (!otherPartyId && currentProfile.user_type === 'tradesman') {
        console.log('No customer_id found, trying job_responses fallback');
        try {
          const { data: jobResponse, error: responseError } = await supabase
            .from('job_responses')
            .select(`
              job_id,
              jobs!inner(customer_id)
            `)
            .eq('job_id', job.id)
            .eq('tradesman_id', currentProfile.id)
            .maybeSingle();

          if (!responseError && jobResponse?.jobs?.customer_id) {
            otherPartyId = jobResponse.jobs.customer_id;
            console.log('Resolved other party ID from job response:', otherPartyId);
          }
        } catch (error) {
          console.error('Job response fallback failed in fetchOtherPartyProfile:', error);
        }
      }
      
      console.log('Other party ID:', otherPartyId);
      
      if (otherPartyId) {
        const { data: otherProfile, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", otherPartyId)
          .maybeSingle();

        console.log('Other party profile:', otherProfile, 'Error:', error);
        
        if (error) {
          console.error("Error fetching other party profile:", error);
          // Don't show error toast for this as it's not critical
        }
        
        if (otherProfile) {
          setOtherPartyProfile(otherProfile);
        } else {
          console.log("Other party profile not found for ID:", otherPartyId);
        }
      }
    } catch (error) {
      console.error("Error fetching other party profile:", error);
    }
  };

  const checkPaymentStatus = async () => {
    if (!job) return;
    
    try {
      const { data, error } = await supabase
        .rpc('get_job_payment_status', { job_id: job.id });

      if (!error && data && data.length > 0 && data[0]?.status === 'completed') {
        setHasCompletedPayment(true);
      } else {
        setHasCompletedPayment(false);
      }
    } catch (error) {
      console.log("Error checking payment status:", error);
      setHasCompletedPayment(false);
    }
  };

  const fetchMessages = async () => {
    if (!job) return;
    
    setIsLoading(true);
    try {
      console.log('Fetching messages for job:', job.id);
      console.log('Current user profile:', currentUserProfile);
      
      // First try a simple query without joins to test RLS
      const { data: simpleData, error: simpleError } = await supabase
        .from("messages")
        .select("*")
        .eq("job_id", job.id)
        .order("created_at", { ascending: true });

      console.log('Simple messages query result:', { data: simpleData, error: simpleError });
      
      if (simpleError) {
        console.error('Simple query failed:', simpleError);
        throw simpleError;
      }
      
      // If simple query works, try the complex query with joins
      if (simpleData && simpleData.length >= 0) {
        const { data, error } = await supabase
          .from("messages")
          .select(`
            *,
            sender:profiles!messages_sender_id_fkey(full_name, user_type),
            recipient:profiles!messages_recipient_id_fkey(full_name, user_type)
          `)
          .eq("job_id", job.id)
          .order("created_at", { ascending: true });

        console.log('Complex messages query result:', { data, error });
        if (error) {
          console.error('Complex query failed, falling back to simple:', error);
          // Fall back to simple data but populate with basic sender info
          const messagesWithBasicInfo = simpleData.map(msg => ({
            ...msg,
            sender: { full_name: 'User', user_type: 'user' },
            recipient: { full_name: 'User', user_type: 'user' }
          }));
          setMessages(messagesWithBasicInfo);
        } else {
          setMessages(data || []);
        }
      } else {
        console.log('No messages found for job');
        setMessages([]);
      }
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Error loading messages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!job) return;

    // Clean up existing subscription
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    const channel = supabase
      .channel(`messages-${job.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `job_id=eq.${job.id}`
        },
        async (payload) => {
          console.log('New message received:', payload);
          
          // Fetch the complete message with relations
          const { data: newMessage, error } = await supabase
            .from("messages")
            .select(`
              *,
              sender:profiles!messages_sender_id_fkey(full_name, user_type),
              recipient:profiles!messages_recipient_id_fkey(full_name, user_type)
            `)
            .eq("id", payload.new.id)
            .maybeSingle();

          if (!error && newMessage) {
            // Add the new message to the state immediately
            setMessages(prev => {
              console.log('JobMessagingDialog: Adding new realtime message:', newMessage.id);
              // Avoid duplicates
              if (prev.find(m => m.id === newMessage.id)) {
                console.log('JobMessagingDialog: Duplicate message detected, skipping');
                return prev;
              }
              return [...prev, newMessage];
            });

            // Get current user profile for toast
            const { data: user } = await supabase.auth.getUser();
            if (user.user) {
              const { data: profile, error: profileError } = await supabase
                .from("profiles")
                .select("id")
                .eq("user_id", user.user.id)
                .maybeSingle();

              // Show toast for messages from other party
              if (!profileError && profile && newMessage.sender_id !== profile.id) {
                toast({
                  title: "New message received",
                  description: `Message from ${newMessage.sender.full_name}`,
                });
              }
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `job_id=eq.${job.id}`
        },
        (payload) => {
          console.log('Message updated:', payload);
          // Update the specific message in state
          setMessages(prev => prev.map(msg => 
            msg.id === payload.new.id 
              ? { ...msg, ...payload.new }
              : msg
          ));
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        setIsOnline(status === 'SUBSCRIBED');
      });

    realtimeChannelRef.current = channel;
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !job || !currentUserProfile) return;

    // Determine recipient - prioritize the recipientId prop for messaging specific tradesmen
    let messageRecipientId = recipientId;
    
    if (!messageRecipientId) {
      // If no specific recipient provided, determine based on user type and job participants
      if (currentUserProfile.user_type === 'customer') {
        // Current user is customer, send to assigned tradesman if available
        messageRecipientId = job.tradesman_id;
      } else if (currentUserProfile.user_type === 'tradesman') {
        // Current user is tradesman, send to customer
        messageRecipientId = job.customer_id;
      }
    }

    // Fallback 1: Use otherPartyProfile if available
    if (!messageRecipientId && otherPartyProfile?.id) {
      messageRecipientId = otherPartyProfile.id;
      console.log('Resolved recipient from otherPartyProfile:', messageRecipientId);
    }

    // Fallback 2: Look at existing messages to find the other party
    if (!messageRecipientId && messages.length > 0) {
      const otherPartyMessage = messages.find(m => m.sender_id !== currentUserProfile.id);
      if (otherPartyMessage) {
        // If current user is sender, other party is recipient; if current user is recipient, other party is sender
        messageRecipientId = otherPartyMessage.sender_id === currentUserProfile.id 
          ? (otherPartyMessage as any).recipient_id 
          : otherPartyMessage.sender_id;
        console.log('Resolved recipient from message history:', messageRecipientId);
      }
    }

    // Fallback 3: If tradesman and we still don't have customer_id on the job, fetch it
    if (!messageRecipientId && currentUserProfile.user_type === 'tradesman') {
      console.log('Recipient missing; fetching full job to resolve customer_id');
      try {
        const { data: fullJob, error: fullJobError } = await supabase
          .from('jobs')
          .select('customer_id, status')
          .eq('id', job.id)
          .maybeSingle();

        if (fullJobError) {
          console.error('Error fetching full job for recipient resolution:', fullJobError);
        }
        if (fullJob?.customer_id) {
          messageRecipientId = fullJob.customer_id;
          console.log('Resolved recipient from full job:', messageRecipientId);
        }
      } catch (error) {
        console.error('Failed to fetch job for recipient resolution:', error);
      }
    }

    // Fallback 4: If tradesman, try using job responses to find customer_id
    if (!messageRecipientId && currentUserProfile.user_type === 'tradesman') {
      console.log('Attempting job response fallback to get customer_id');
      try {
        // Try finding customer_id from job responses - this should work for tradesmen who have responded
        const { data: jobResponse, error: responseError } = await supabase
          .from('job_responses')
          .select(`
            job_id,
            jobs!inner(customer_id)
          `)
          .eq('job_id', job.id)
          .eq('tradesman_id', currentUserProfile.id)
          .maybeSingle();

        if (responseError) {
          console.error('Job response lookup failed:', responseError);
        } else if (jobResponse?.jobs?.customer_id) {
          messageRecipientId = jobResponse.jobs.customer_id;
          console.log('Resolved recipient from job response:', messageRecipientId);
        }
      } catch (error) {
        console.error('Job response fallback failed:', error);
      }
    }

    console.log('Send message debug:', {
      currentUserProfile: currentUserProfile.id,
      jobCustomerId: job.customer_id,
      jobTradesmanId: job.tradesman_id,
      recipientIdProp: recipientId,
      finalRecipientId: messageRecipientId,
      otherPartyProfileId: otherPartyProfile?.id,
      messagesCount: messages.length
    });

    if (!messageRecipientId) {
      toast({
        title: "Error",
        description: "Unable to detect message recipient",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-message', {
        body: {
          job_id: job.id,
          recipient_id: messageRecipientId,
          content: newMessage.trim(),
          message_type: 'text'
        }
      });

      if (error) throw error;

      setNewMessage("");
      
      // Don't show toast for successful send since the message will appear in chat immediately
    } catch (error: any) {
      if (error.message.includes("blocked")) {
        toast({
          title: "Message blocked",
          description: "Messages containing contact information are not allowed. Please use only the in-app messaging system.",
          variant: "destructive",
        });
      } else if (error.message.includes("contacted you first")) {
        toast({
          title: "Cannot initiate conversation",
          description: "You can only message customers who have contacted you first about this job.",
          variant: "destructive",
        });
      } else if (error.message.includes("only message tradesmen about your own jobs")) {
        toast({
          title: "Unauthorized",
          description: "You can only message tradesmen about your own jobs.",
          variant: "destructive",
        });
      } else if (error.message.includes("qualified, active tradesmen")) {
        toast({
          title: "Cannot message tradesman",
          description: "This tradesman is not currently available for messaging.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error sending message",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startChatSession = async () => {
    if (!job) return;

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Insert or update active chat session
      await supabase
        .from('active_chat_sessions')
        .upsert({
          job_id: job.id,
          user_id: user.user.id,
          last_seen: new Date().toISOString()
        }, {
          onConflict: 'job_id,user_id'
        });

      // Set up interval to update last_seen every 30 seconds
      const updateInterval = setInterval(async () => {
        await supabase
          .from('active_chat_sessions')
          .upsert({
            job_id: job.id,
            user_id: user.user.id,
            last_seen: new Date().toISOString()
          }, {
            onConflict: 'job_id,user_id'
          });
      }, 30000);

      // Store interval ID for cleanup
      (window as any).chatSessionInterval = updateInterval;
    } catch (error) {
      console.error('Failed to start chat session:', error);
    }
  };

  const updateChatSession = async () => {
    if (!job) return;
    
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      console.log('JobMessagingDialog: Updating chat session timestamp');
      // Update last seen timestamp
      await supabase
        .from('active_chat_sessions')
        .upsert({
          job_id: job.id,
          user_id: user.user.id,
          last_seen: new Date().toISOString()
        }, {
          onConflict: 'job_id,user_id'
        });
    } catch (error) {
      console.error('Error updating chat session:', error);
    }
  };

  const endChatSession = async () => {
    try {
      // Clear the update interval
      if ((window as any).chatSessionInterval) {
        clearInterval((window as any).chatSessionInterval);
        delete (window as any).chatSessionInterval;
      }

      const { data: user } = await supabase.auth.getUser();
      if (!user.user || !job) return;

      // Remove active chat session
      await supabase
        .from('active_chat_sessions')
        .delete()
        .eq('job_id', job.id)
        .eq('user_id', user.user.id);
    } catch (error) {
      console.error('Failed to end chat session:', error);
    }
  };


  const markMessagesAsRead = async () => {
    if (!job || !currentUserProfile) return;

    try {
      const { error } = await supabase
        .from("messages")
        .update({ is_read_by_recipient: true })
        .eq("job_id", job.id)
        .eq("recipient_id", currentUserProfile.id)
        .eq("is_read_by_recipient", false);

      if (error) {
        console.error("Error marking messages as read:", error);
      }
    } catch (error) {
      console.error("Error in markMessagesAsRead:", error);
    }
  };

  const getOtherPartyName = () => {
    if (!currentUserProfile || !job) return "Other Party";
    
    // If we have the other party profile, use their name
    if (otherPartyProfile) {
      return otherPartyProfile.company_name || otherPartyProfile.full_name || "Other Party";
    }
    
    // Fall back to finding name from messages
    const otherMessage = messages.find(m => 
      m.sender?.user_type && m.sender?.user_type !== currentUserProfile.user_type
    );
    
    if (otherMessage?.sender?.full_name) {
      return otherMessage.sender.full_name;
    }
    
    return currentUserProfile.user_type === 'customer' ? 'Tradesman' : 'Customer';
  };


  // Don't render if we don't have essential data yet
  if (!job || !currentUserProfile) {
    return null;
  }

  return (
    <Dialog open={!!job && !!currentUserProfile} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'w-screen h-screen max-w-none m-0 rounded-none' : 'w-[95vw] sm:max-w-2xl h-[85vh] sm:h-[80vh] mx-2 sm:mx-auto'} flex flex-col`}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-primary" />
                Live Chat: {job.title}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    isOnline ? "bg-green-500" : "bg-red-500"
                  )} />
                  <span className={isOnline ? "text-green-600" : "text-red-600"}>
                    {isOnline ? "Connected" : "Disconnected"}
                  </span>
                </div>
                  <span className="text-muted-foreground">•</span>
                Chat with {getOtherPartyName()}
{otherPartyProfile && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-primary">
                        {otherPartyProfile.company_name || otherPartyProfile.full_name}
                      </span>
                      {otherPartyProfile.user_type === 'tradesman' && otherPartyProfile.trade_specialties && (
                        <Badge variant="secondary" className="text-xs">
                          {otherPartyProfile.trade_specialties[0]}
                        </Badge>
                      )}
                      {otherPartyProfile.user_type === 'tradesman' && otherPartyProfile.average_rating && (
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500">⭐</span>
                          <span className="text-xs font-medium">{otherPartyProfile.average_rating.toFixed(1)}</span>
                          <span className="text-xs text-muted-foreground">
                            ({otherPartyProfile.total_reviews} reviews)
                          </span>
                        </div>
                      )}
                      {otherPartyProfile.location && (
                        <span className="text-xs text-muted-foreground">📍 {otherPartyProfile.location}</span>
                      )}
                      {hasCompletedPayment && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">
                            Contact: {otherPartyProfile.email} | {otherPartyProfile.phone}
                          </span>
                        </>
                      )}
                    </div>
                  </>
                )}
              </DialogDescription>
            </div>
            {/* Report Buttons - Different styling for customers vs tradesmen */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 mr-8">
              {currentUserProfile && job && currentUserProfile.id === job.customer_id && (
                <>
                  <Button
                    onClick={() => {
                      console.log('Customer reporting tradesman conversation');
                      setShowReportDialog(true);
                      toast({ 
                        title: 'Report Chat', 
                        description: 'Report this conversation to administrators.' 
                      });
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 w-full sm:w-auto justify-center"
                  >
                    <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                    <span className="text-xs sm:text-sm">Report Chat</span>
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('Customer reporting tradesman account');
                      setShowAccountReportDialog(true);
                      toast({ 
                        title: 'Report Account', 
                        description: 'Report this tradesman account to administrators.' 
                      });
                    }}
                    variant="outline"
                    size="sm"
                    className="text-red-700 border-red-300 bg-red-100 hover:bg-red-200 hover:border-red-400 w-full sm:w-auto justify-center"
                  >
                    <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                    <span className="text-xs sm:text-sm">Report Account</span>
                  </Button>
                </>
              )}
              {currentUserProfile?.user_type === 'tradesman' && (
                <Button
                  onClick={() => {
                    console.log('Tradesman reporting customer');
                    setShowReportDialog(true);
                    toast({ 
                      title: 'Report Customer', 
                      description: 'Report this customer and conversation to administrators.' 
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/20 hover:bg-destructive/10 hover:border-destructive w-full sm:w-auto justify-center"
                >
                  <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Report Customer</span>
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>


        {/* Messages Area */}
        <ScrollArea className={`flex-1 ${isMobile ? 'px-1' : 'pr-4'}`} ref={scrollAreaRef}>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">Start the conversation</h3>
              <p className="text-sm text-muted-foreground">
                Send a message to coordinate the job details securely.
              </p>
            </div>
          ) : (
            <div className={`space-y-4 ${isMobile ? 'py-2' : 'py-4'}`}>
              {messages.map((message) => {
                const isOwn = currentUserProfile?.id === message.sender_id;
                const isBlocked = message.is_blocked;
                
                // Debug photo messages
                if (message.message_type === 'photo') {
                  console.log('Photo message debug:', {
                    id: message.id,
                    message_type: message.message_type,
                    photo_url: message.photo_url,
                    content: message.content
                  });
                }
                
                return (
                  <div
                    key={message.id}
                    className={cn(
                      "flex group",
                      isOwn ? "justify-end" : "justify-start"
                    )}
                  >
                    <div className={`flex items-end gap-2 ${isMobile ? 'max-w-[95%]' : 'max-w-[70%]'} ${!isOwn && isMobile ? 'ml-1' : ''} ${isOwn && isMobile ? 'mr-1' : ''}`}>
                      <div
                        className={cn(
                          `rounded-lg ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} relative`,
                          isOwn
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted",
                          isBlocked && "bg-destructive/10 border border-destructive/20"
                        )}
                      >
                        {!isOwn && (
                          <div className="flex items-center gap-2 mb-1">
                            {message.sender?.user_type === 'tradesman' && otherPartyProfile?.logo_url && (
                              <TradesmanLogo 
                                logoUrl={otherPartyProfile.logo_url}
                                companyName={otherPartyProfile.company_name}
                                size="sm"
                              />
                            )}
                            <UserCheck className={`${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                            <p className={`${isMobile ? 'text-xs' : 'text-xs'} font-medium`}>
                              {message.sender?.full_name}
                            </p>
                            <Badge variant="secondary" className={`${isMobile ? 'text-xs' : 'text-xs'}`}>
                              {message.sender?.user_type}
                            </Badge>
                          </div>
                        )}
                        
                        {isBlocked ? (
                          <div className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                            <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
                              Message blocked: {message.blocked_reason}
                            </span>
                          </div>
                        ) : (message.message_type === 'photo' && message.photo_url) ? (
                          <div className="space-y-2">
                            <img 
                              src={message.photo_url} 
                              alt="Shared photo"
                              className="max-w-xs rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setPhotoViewerUrl(message.photo_url!)}
                              style={{ maxHeight: isMobile ? '120px' : '200px' }}
                            />
                            <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground`}>Click to view full size</p>
                            {message.content !== '[Photo]' && (
                              <p className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{message.content}</p>
                            )}
                          </div>
                        ) : (
                          <p className={`${isMobile ? 'text-xs' : 'text-sm'} whitespace-pre-wrap`}>{message.content}</p>
                        )}
                        
                        <p
                          className={cn(
                            `${isMobile ? 'text-xs' : 'text-xs'} mt-1`,
                            isOwn
                              ? "text-primary-foreground/70"
                              : "text-muted-foreground"
                          )}
                        >
                          {format(new Date(message.created_at), "h:mm a")}
                        </p>
                      </div>

                    </div>
                  </div>
                );
              })}

              {/* Typing Indicator */}
              {typingIndicator && (
                <div className="flex justify-start">
                  <div className={`bg-muted rounded-lg px-3 py-2 ${isMobile ? 'max-w-[95%] ml-1' : 'max-w-[70%]'}`}>
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">{typingIndicator} is typing...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Message Input */}
        <div className="flex gap-2 flex-shrink-0 pt-4 border-t">
          <Button
            onClick={() => setShowPhotoUpload(true)}
            variant="outline"
            size="icon"
            className="flex-shrink-0"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message... (no phone numbers or emails)"
            disabled={isSending}
            className="flex-1"
          />
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isSending}
            size="icon"
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {/* Policy Warning */}
        <div className={`${isMobile ? 'px-1 py-1' : 'px-2 py-2'} text-center`}>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-amber-600 dark:text-amber-400 font-medium`}>
            ⚠️ Do not share phone numbers or email addresses in chat
          </p>
        </div>

        {/* Report Dialogs */}
        <ReportChatDialog
          jobId={job.id}
          reportedUserId={otherPartyProfile?.id || recipientId || ""} // Simplified logic - let backend determine if needed
          reportedUserName={otherPartyProfile?.full_name || getOtherPartyName()}
          isOpen={showReportDialog}
          onClose={() => setShowReportDialog(false)}
        />
        
        {/* Report Tradesman Account Dialog - Only for customers */}
        {currentUserProfile?.user_type === 'customer' && (
          <ReportTradesmanAccountDialog
            jobId={job.id}
            tradesmanId={otherPartyProfile?.id || recipientId || job.tradesman_id || ""}
            tradesmanName={otherPartyProfile?.full_name || 'Tradesman'}
            isOpen={showAccountReportDialog}
            onClose={() => setShowAccountReportDialog(false)}
          />
        )}
        
        <ChatPhotoUpload
          open={showPhotoUpload}
          onOpenChange={setShowPhotoUpload}
          jobId={job.id}
          recipientId={recipientId || (currentUserProfile?.user_type === 'customer' ? job.tradesman_id : job.customer_id)}
          onPhotoUploaded={() => {
            console.log('JobMessagingDialog: Photo uploaded - closing dialog (realtime will handle message)');
            // Don't fetchMessages() here - realtime subscription will handle the new message
            setShowPhotoUpload(false);
          }}
        />
        
        {/* Photo Viewer Modal */}
        <Dialog open={!!photoViewerUrl} onOpenChange={() => setPhotoViewerUrl(null)}>
          <DialogContent className="max-w-4xl w-full p-0">
            <div className="relative">
              {photoViewerUrl && (
                <img 
                  src={photoViewerUrl} 
                  alt="Full size photo"
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Photo Viewer Modal */}
        <Dialog open={!!photoViewerUrl} onOpenChange={() => setPhotoViewerUrl(null)}>
          <DialogContent className="max-w-4xl w-full p-0">
            <div className="relative">
              {photoViewerUrl && (
                <img 
                  src={photoViewerUrl} 
                  alt="Full size photo"
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setPhotoViewerUrl(null)}
              >
                ✕
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};